import { Sentry } from 'lib-shared-sentry';
import type { AnyAction, Dispatch } from 'redux';

import { appActions } from 'app/app.actions';

import * as errorsActions from '../../../../components/errors/actions/errors';
import { notificationActions } from '../../../../components/notifications/actions/notification';
import { twilioActions } from '../actions/twilioActions';

// 31208 - The user denied access to the microphone
export const twilioErrorCodeMicrophoneDenied = 31208;
// 31003 - The server could not produce a response within a suitable amount of time
export const twilioErrorCodeConnectionTimeout = 31003;
// 31205 - JWT token expired.
export const twilioErrorCodeConnectionJwtTokenExpired = 31205;
// 31005 - A connection error occurred during the call
const twilioErrorCodeConnectionError = 31005;
// 31000 - An unknown error occurred
const twilioErrorCodeUnknownError = 31000;
// 20104 - Access Token has expired or expiration date is invalid
const twilioErrorCodeAccessTokenExpired = 20104;
// 31009 - No transport available to send or receive messages.
const twilioErrorCodeNoTransportAvailable = 31009;
// 31206 - Rate exceeded authorized limit.
const twilioErrorCodeRateExceeded = 31206;
// 53000 - Signaling connection error
const twilioErrorCodeSignalingConnectionError = 53000;
// 20101 - Twilio was unable to validate your Access Token
const twilioErrorCodeInvalidAccessToken = 20101;

export const handledTwilioErrorCodes = [
  twilioErrorCodeMicrophoneDenied,
  twilioErrorCodeConnectionTimeout,
  twilioErrorCodeConnectionJwtTokenExpired,
];

export const ignoredTwilioErrorCodes = [
  twilioErrorCodeConnectionError,
  twilioErrorCodeUnknownError,
  twilioErrorCodeAccessTokenExpired,
  twilioErrorCodeNoTransportAvailable,
  twilioErrorCodeRateExceeded,
  twilioErrorCodeSignalingConnectionError,
  twilioErrorCodeInvalidAccessToken,
];

/**
 * Returns true for error codes that require the Twilio Device to be destroyed.
 * Used by TwilioRuntime to handle the SDK-level consequence of certain errors
 * before notifying the outside world via the event handler.
 */
export function shouldDestroyDeviceOnError(code: number): boolean {
  return (
    code === twilioErrorCodeConnectionTimeout ||
    code === twilioErrorCodeConnectionJwtTokenExpired ||
    code === twilioErrorCodeInvalidAccessToken
  );
}

const { notificationAdd } = notificationActions;
const { errorMicrophoneErrorShowModal } = errorsActions.actions;
const { twilioDeviceError } = twilioActions;

type DeviceErrorDeps = {
  error: { code: number; message: string };
  dispatch: Dispatch<AnyAction>;
};

export function handleDeviceError({ error, dispatch }: DeviceErrorDeps): void {
  dispatch(twilioDeviceError({ code: error.code, message: error.message }));

  const errorMsg = `Twilio client error (${error.code}): ${error.message}`;
  console.error(errorMsg);

  if (
    !handledTwilioErrorCodes.includes(error.code) &&
    !ignoredTwilioErrorCodes.includes(error.code)
  ) {
    Sentry.captureMessage(errorMsg, 'error');
  } else {
    Sentry.addBreadcrumb({
      category: 'twilio',
      message: errorMsg,
      level: 'error',
    });
  }

  if (error.code === twilioErrorCodeMicrophoneDenied) {
    dispatch(
      notificationAdd({
        title: 'Microphone denied',
        text: error.message,
        className: 'danger',
        icon: 'announcement-slash',
      }),
    );
    dispatch(errorMicrophoneErrorShowModal());
    dispatch(
      appActions.showModal({
        modal: { type: 'app:microphoneError', arg: {} },
      }),
    );
  }
}
